import React, { useState, useEffect, useCallback } from 'react';
import API from '../../services/api';
import BikeLifeCycleView from './BikeLifeCycleView';

const BikeHistoryPanel = () => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [search, setSearch] = useState('');
    const [stockStatus, setStockStatus] = useState('ALL'); 
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [selectedBikeId, setSelectedBikeId] = useState(null);

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get('/v1/inventory/bike-history', {
                params: {
                    page,
                    limit,
                    search,
                    stock_status: stockStatus,
                    start_date: startDate,
                    end_date: endDate
                }
            });
            if (res.data.success) {
                setRecords(res.data.data);
                setTotalPages(res.data.totalPages);
                setTotalRecords(res.data.total);
            }
        } catch (err) {
            console.error('Error fetching bike history:', err);
        } finally {
            setLoading(false);
        }
    }, [page, limit, search, stockStatus, startDate, endDate]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const getDateColumnTitle = () => {
        if (stockStatus === 'Sold') return 'Sale Date';
        if (stockStatus === 'Available') return 'Purchase Date';
        return 'Purchase / Sale Date';
    };

    return (
        <div style={styles.container}>
            <div style={styles.headerCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                    <div>
                        <h3 style={{ margin: 0, color: '#0f172a', fontSize: '20px', fontWeight: '800' }}>🏍️ Bike Inventory & History</h3>
                        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>Track stock purchases, sales, and vehicle lifecycle details.</p>
                    </div>
                    <span style={styles.badgeCount}>Total Records: {totalRecords}</span>
                </div>

                <div style={styles.filterRow}>
                    <input
                        type="text"
                        placeholder="Search Model, Engine, Chassis..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        style={styles.input}
                    />

                    {/* ⚡ Only Available and Sold options */}
                    <select 
                        value={stockStatus} 
                        onChange={(e) => { setStockStatus(e.target.value); setPage(1); }}
                        style={styles.select}
                    >
                        <option value="ALL">All Status</option>
                        <option value="Available">Available</option>
                        <option value="Sold">Sold</option>
                    </select>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>From:</span>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                            style={styles.inputDate}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>To:</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                            style={styles.inputDate}
                        />
                    </div>

                    {(search || stockStatus !== 'ALL' || startDate || endDate) && (
                        <button 
                            onClick={() => { setSearch(''); setStockStatus('ALL'); setStartDate(''); setEndDate(''); setPage(1); }}
                            style={styles.resetBtn}
                        >
                            Reset Filters
                        </button>
                    )}
                </div>
            </div>

            <div style={styles.tableCard}>
                {loading ? (
                    <div style={{ padding: '50px', textAlign: 'center', color: '#64748b' }}>Loading records...</div>
                ) : records.length === 0 ? (
                    <div style={{ padding: '50px', textAlign: 'center', color: '#94a3b8' }}>No bike records found matching criteria.</div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.thRow}>
                                <th style={styles.th}>{getDateColumnTitle()}</th>
                                <th style={styles.th}>Bike Model / Details</th>
                                <th style={styles.th}>Engine / Chassis</th>
                                <th style={styles.th}>Supplier / Party</th>
                                <th style={styles.th}>Price</th>
                                <th style={styles.th}>Status</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.map((item) => {
                                const isAvailable = item.stock_status === 'Available';
                                const displayDate = item.stock_status === 'Sold' ? (item.sale_date || 'N/A') : (item.purchase_date || 'N/A');
                                
                                return (
                                    <tr key={item.stock_id} style={styles.tr}>
                                        <td style={styles.td}>
                                            <div style={{ fontWeight: '600', color: '#334155' }}>{displayDate}</div>
                                            <div style={{ fontSize: '11px', color: '#64748b' }}>{item.purchase_type || '-'}</div>
                                        </td>
                                        <td style={styles.td}>
                                            <strong style={{ color: '#0f172a' }}>{item.model_name}</strong> ({item.model_year})
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>{item.color} | {item.cc} CC | {item.version_type || 'Standard'}</div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ fontSize: '13px' }}><strong>Eng:</strong> {item.engine_number}</div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}><strong>Ch:</strong> {item.chassis_number}</div>
                                        </td>
                                        <td style={styles.td}>
                                            {item.supplier_name || item.customer_name || 'Initial Stock'}
                                        </td>
                                        <td style={styles.td}>
                                            <strong style={{ color: '#059669' }}>
                                                Rs. {Number(item.price || 0).toLocaleString()}
                                            </strong>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={isAvailable ? styles.availTag : styles.soldTag}>
                                                {item.stock_status}
                                            </span>
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            <button 
                                                onClick={() => setSelectedBikeId(item.bike_master_id)}
                                                style={styles.detailBtn}
                                            >
                                                🔍 Details
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                <div style={styles.paginationContainer}>
                    <div style={{ color: '#64748b', fontSize: '13px' }}>
                        Showing Page <strong>{page}</strong> of <strong>{totalPages}</strong> (Total: {totalRecords} records)
                    </div>

                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <select 
                            value={limit} 
                            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                            style={styles.limitSelect}
                        >
                            <option value={10}>10 per page</option>
                            <option value={25}>25 per page</option>
                            <option value={50}>50 per page</option>
                        </select>

                        <button
                            disabled={page === 1}
                            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                            style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}
                        >
                            Previous
                        </button>

                        <button
                            disabled={page >= totalPages}
                            onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                            style={page >= totalPages ? styles.pageBtnDisabled : styles.pageBtn}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {selectedBikeId && (
                <BikeLifeCycleView 
                    bikeId={selectedBikeId} 
                    onClose={() => setSelectedBikeId(null)} 
                />
            )}
        </div>
    );
};

const styles = {
    container: { display: 'flex', flexDirection: 'column', gap: '20px' },
    headerCard: { background: '#fff', padding: '24px', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
    badgeCount: { background: '#f1f5f9', color: '#334155', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' },
    filterRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginTop: '15px' },
    input: { flex: 1, minWidth: '220px', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' },
    select: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff', outline: 'none' },
    inputDate: { padding: '9px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' },
    resetBtn: { padding: '10px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
    tableCard: { background: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' },
    thRow: { background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
    th: { padding: '14px 16px', color: '#475569', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px', textTransform: 'uppercase' },
    tr: { borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' },
    td: { padding: '14px 16px', color: '#334155', verticalAlign: 'middle' },
    availTag: { background: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' },
    soldTag: { background: '#dbeafe', color: '#1e40af', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' },
    detailBtn: { background: '#0284c7', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' },
    paginationContainer: { padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', background: '#fff' },
    limitSelect: { padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' },
    pageBtn: { padding: '8px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
    pageBtnDisabled: { padding: '8px 16px', background: '#e2e8f0', color: '#94a3b8', border: 'none', borderRadius: '6px', cursor: 'not-allowed', fontSize: '13px' }
};

export default BikeHistoryPanel;